/**
 * Learning Hooks
 * 
 * React Query hooks for courses, videos, materials, and blog.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { learningService } from '@/services/learning.service';
import { toast } from '@/hooks/use-toast';

// Query keys
export const learningKeys = {
  all: ['learning'] as const,
  courses: () => [...learningKeys.all, 'courses'] as const,
  coursesList: (search?: string, page?: number) => [...learningKeys.courses(), { search, page }] as const,
  course: (id: string) => [...learningKeys.courses(), id] as const,
  lesson: (courseId: string, lessonId: string) => [...learningKeys.courses(), courseId, 'lesson', lessonId] as const,
  myCourses: (status?: string, page?: number) => [...learningKeys.all, 'myCourses', { status, page }] as const,
  videos: (search?: string, page?: number) => [...learningKeys.all, 'videos', { search, page }] as const,
  video: (id: string) => [...learningKeys.all, 'video', id] as const,
  materials: (search?: string, page?: number) => [...learningKeys.all, 'materials', { search, page }] as const,
  blog: () => [...learningKeys.all, 'blog'] as const,
  blogPosts: (search?: string, page?: number) => [...learningKeys.blog(), { search, page }] as const,
  blogPost: (slug: string) => [...learningKeys.blog(), slug] as const,
};

// Courses
export function useCourses(search?: string, page: number = 1, pageSize: number = 12) {
  return useQuery({
    queryKey: learningKeys.coursesList(search, page),
    queryFn: () => learningService.getCourses(search, page, pageSize),
  });
}

export function useCourseDetails(courseId: string) {
  return useQuery({
    queryKey: learningKeys.course(courseId),
    queryFn: () => learningService.getCourseDetails(courseId),
    enabled: !!courseId,
  });
}

export function useLesson(courseId: string, lessonId: string) {
  return useQuery({
    queryKey: learningKeys.lesson(courseId, lessonId),
    queryFn: () => learningService.getLesson(courseId, lessonId),
    enabled: !!courseId && !!lessonId,
  });
}

export function useEnrollInCourse() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (courseId: string) => learningService.enrollInCourse(courseId),
    onSuccess: (_, courseId) => {
      queryClient.invalidateQueries({ queryKey: learningKeys.course(courseId) });
      queryClient.invalidateQueries({ queryKey: learningKeys.myCourses() });
      toast({
        title: 'Matrícula realizada!',
        description: 'Você agora tem acesso ao curso.',
      });
    },
    onError: () => {
      toast({
        title: 'Erro ao matricular',
        description: 'Tente novamente mais tarde.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateLessonProgress() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      courseId,
      lessonId,
      progressPercentage,
      lastPosition,
      isCompleted,
    }: {
      courseId: string;
      lessonId: string;
      progressPercentage: number;
      lastPosition?: number;
      isCompleted?: boolean;
    }) => learningService.updateLessonProgress(courseId, lessonId, progressPercentage, lastPosition, isCompleted),
    onSuccess: (_, { courseId }) => {
      queryClient.invalidateQueries({ queryKey: learningKeys.course(courseId) });
    },
  });
}

// My Courses
export function useMyCourses(status?: 'in_progress' | 'completed' | 'all', page: number = 1) {
  return useQuery({
    queryKey: learningKeys.myCourses(status, page),
    queryFn: () => learningService.getMyCourses(status, page),
  });
}

// Videos (Vídeo-aulas)
export function useVideos(search?: string, page: number = 1) {
  return useQuery({
    queryKey: learningKeys.videos(search, page),
    queryFn: () => learningService.getVideos(search, page),
  });
}

export function useVideo(videoId: string) {
  return useQuery({
    queryKey: learningKeys.video(videoId),
    queryFn: () => learningService.getVideo(videoId),
    enabled: !!videoId,
  });
}

export function useVideoPlayback(videoId: string) {
  return useQuery({
    queryKey: [...learningKeys.all, 'video-playback', videoId],
    queryFn: () => learningService.getVideoPlayback(videoId),
    enabled: !!videoId,
    // O link expira: não vale reaproveitar de sessões antigas.
    staleTime: 0,
    gcTime: 0,
  });
}

// Materials
export function useMaterials(search?: string, page: number = 1) {
  return useQuery({
    queryKey: learningKeys.materials(search, page),
    queryFn: () => learningService.getMaterials(search, page),
  });
}

// Blog
// A home mostra 1 artigo no bloco de destaque e lista todo o resto na grade —
// 21 por página dá 20 cards (linhas completas de 3 no desktop) além do destaque.
export const BLOG_PAGE_SIZE = 21;

export function useBlogPosts(search?: string, page: number = 1, category?: string) {
  return useQuery({
    queryKey: [...learningKeys.blogPosts(search, page), { category }],
    queryFn: () => learningService.getBlogPosts(search, page, BLOG_PAGE_SIZE, category),
  });
}

export function useBlogHighlights() {
  return useQuery({
    queryKey: [...learningKeys.blog(), 'highlights'],
    queryFn: () => learningService.getBlogHighlights(),
  });
}

export function useBlogCategories() {
  return useQuery({
    queryKey: [...learningKeys.blog(), 'categories'],
    queryFn: () => learningService.getBlogCategories(),
  });
}

export function useBlogPost(slug: string) {
  return useQuery({
    queryKey: learningKeys.blogPost(slug),
    queryFn: () => learningService.getBlogPost(slug),
    enabled: !!slug,
  });
}

